import { AerialPlotResponse } from "./types";

/**
 * Labels the API resolves to a stored document. The frontend never names a file —
 * the server owns the mapping (`AzureBlobBusinessLogic.GetDocumentSasUri`), so a
 * document can be replaced or renamed in storage with no frontend change.
 */
export const DOCUMENT_TYPES = {
	ANOVA: "ANOVA_DOCUMENTATION",
	FIELD_LAYOUT_TEMPLATE: "FIELD_LAYOUT_TEMPLATE",
	ORTHO_UPLOAD: "ORTHO_UPLOAD_DOCUMENTATION",
} as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[keyof typeof DOCUMENT_TYPES];

//-- aerialPlotConnections is substituted into process.env by webpack's DefinePlugin, so it is not
//-- covered by the ProcessEnv typings; narrow just the field this module reads.
const connections = process.env.aerialPlotConnections as unknown as { aerialImageApi: { baseUrl: string } };

const imageApiUri = connections.aerialImageApi.baseUrl + "api/v1/";

/**
 * Fetches a time-limited SAS URL for the given document type.
 */
export async function getDocumentUrl(
	documentType: DocumentType,
	clientId: string,
	accessToken: string,
): Promise<string> {
	const response = await fetch(
		`${imageApiUri}Download/GetDocumentSASUri?clientId=${clientId}&documentType=${documentType}`,
		{
			headers: {
				Authorization: `Bearer ${accessToken}`,
			},
		},
	);

	const body = (await response.json()) as AerialPlotResponse<string | null> | null;

	if (body?.statusCode !== 200 || !body.data) {
		throw new Error(`No link was returned for document type ${documentType}`);
	}

	return body.data;
}

/**
 * Content types the browser can render in a tab, keyed by the file extension in the document's
 * URL. Used only when storage does not report a usable type of its own.
 */
const INLINE_CONTENT_TYPES: Record<string, string> = {
	gif: "image/gif",
	jpeg: "image/jpeg",
	jpg: "image/jpeg",
	pdf: "application/pdf",
	png: "image/png",
	txt: "text/plain",
};

/**
 * Resolves the content type to hand the browser. Storage usually reports one, but blobs uploaded
 * without it fall back to `application/octet-stream`, which every browser treats as a download.
 */
function inlineContentType(blobType: string, documentUrl: string): string {
	if (blobType && blobType !== "application/octet-stream") return blobType;

	const extension = new URL(documentUrl).pathname.split(".").pop()?.toLowerCase() ?? "";

	return INLINE_CONTENT_TYPES[extension] ?? blobType;
}

/**
 * Fetches the document's bytes and wraps them in an object URL suitable for viewing in a tab.
 *
 * The SAS URL cannot be viewed directly: storage serves it with a `Content-Disposition` that forces
 * a download, and the response headers that would override that are covered by the SAS signature,
 * so they cannot be appended client-side. Re-serving the same bytes from a blob URL drops the
 * disposition, leaving the browser to render them by content type.
 *
 * The caller owns the returned URL and must revoke it once the document has loaded.
 */
export async function getDocumentObjectUrl(
	documentType: DocumentType,
	clientId: string,
	accessToken: string,
): Promise<string> {
	const documentUrl = await getDocumentUrl(documentType, clientId, accessToken);

	//-- no-store keeps the browser from revalidating with conditional headers, which Azure rejects.
	const response = await fetch(documentUrl, { cache: "no-store" });

	if (!response.ok) {
		throw new Error(`Document type ${documentType} could not be retrieved (${response.status})`);
	}

	const blob = await response.blob();
	const contentType = inlineContentType(blob.type, documentUrl);

	//-- slice re-labels the bytes without copying them when storage reported an unusable type.
	return URL.createObjectURL(contentType === blob.type ? blob : blob.slice(0, blob.size, contentType));
}
